import { AxiosError } from 'axios';
import { http } from '../http';

export interface Endereco {
  rua: string;
  numero: string;
  complemento?: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
}

export interface Carro {
  addedToSga: boolean;
  placa: string;
  anoModelo: number;
  anoFabricacao: number;
  modelo: string;
  chassi: string;
  renavam: string;
  kmRodados: number;
  corId: string;
  combustivelId: string;
  cambioId: string;
  tipoVeiculoId: string;
  categoriaId: string;
  cooperativaId: string;
  cotaId: string;
  vencimentoDesejado: number;
  endereco: Endereco;
}

export interface AssociadoData {
  nomeCompleto: string;
  cpfCnpj: string;
  rg: string;
  dataNascimento: string;
  sexoId: string;
  email: string;
  dddCelular: string;
  celular: string;
  regionalId: string;
  endereco: Endereco;
  carro: Carro;
}

export interface PaginatedResponse<T> {
  totalItems: number;
  page: number;
  limit: number;
  items: T[];
}

export interface Associado {
  objCategorias: any;
  id: string;
  addedToSga: boolean;
  codigoAssociado: string;
  nomeCompleto: string;
  /** at-83: nome do consultor dono da carteira (só em `buscar-por-cpf-cnpj`). */
  consultorNome?: string | null;
  cpfCnpj: string;
  rg: string;
  dataNascimento: string;
  sexo: string;
  email: string;
  dddCelular: string;
  celular: string;
  regional: string;
  regionalNome?: string;
  cep: string;
  rua: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
  createdAt: string;
  updatedAt: string;
  carros?: {
    anoModelo: any;
    anoFabricacao(anoFabricacao: any): string;
    id: string;
    addedToSga: boolean;
    placa: string;
    tipoVeiculo: string;
    cooperativa?: string;
    valorFipe?: number;
    // at-30 (item 18): base dos lookups por faixa (fallback: valorFipe)
    valorProtegido?: number | null;
    // usados no cálculo da mensalidade exibida na seleção de produtos (at-21)
    valorCota?: number;
    cota?: string;
    deletedAt?: string | null;
    // at-37 (item 39): ATIVO | SUBSTITUIDO. Use `isCarroVivo` (utils/carroSituacao)
    // em vez de checar só `deletedAt` — o substituído tem deletedAt nulo.
    situacao?: string | null;
  }[];
}

export interface AssociadoModal {
  id: string | null;
  addedToSga: boolean;
  nomeCompleto: string;
  cpfCnpj: string;
  rg: string;
  dataNascimento: string;
  sexoId: string;
  email: string;
  dddCelular: string;
  celular: string;
  regionalId: string;
  endereco: {
    rua: string;
    numero: string;
    complemento: string;
    bairro: string;
    cidade: string;
    estado: string;
    cep: string;
  };
}

export interface SearchAssociadosResponse {
  total: number;
  page: number;
  limit: number;
  items: Array<Pick<
    Associado,
    'id' | 'codigoAssociado' | 'nomeCompleto' | 'cpfCnpj' | 'email' | 'regional' | 'createdAt'
  >>;
}

export const createAssociado = async (associadoData: any) => {
  try {
    const response = await http.post('/associado/cadastrar', associadoData);

    return response.data;
  } catch (error: unknown) {
    const axiosError = error as AxiosError;
    throw axiosError;
  }
};

export const updateAssociado = async (associadoData: any) => {
  try {
    const response = await http.patch(
      `/associado/editar-associado/${associadoData.cpfCnpj}`,
      associadoData
    );

    return response.data;
  } catch (error: unknown) {
    const axiosError = error as AxiosError;
    throw axiosError;
  }
};

export const fetchAssociadosPorVolunteerCode = async (
  volunteerCode: string,
  page: number = 1,
  limit: number = 10,
  busca?: string
): Promise<{ totalItems: number; items: Associado[] }> => {
  if (!volunteerCode) {
    throw new Error("O volunteerCode é obrigatório.");
  }

  try {
    const response = await http.get('/associado/listar-por-voluntario', {
      params: {
        volunteerCode,
        page,
        limit,
        sortOrder: 'desc',
        // filtro por nome/CPF/placa/modelo; omitido quando vazio
        ...(busca?.trim() ? { busca: busca.trim() } : {}),
      },
    });

    return response.data;
  } catch (error: unknown) {
    const axiosError = error as AxiosError<any>;
    throw new Error(axiosError.response?.data?.message || 'Erro ao buscar associados');
  }
};

export const fetchAssociadoPorId = async (id: string): Promise<Associado> => {
  if (!id) {
    throw new Error("O ID do associado é obrigatório.");
  }

  try {
    const response = await http.get(`/associado/buscar/${id}`);

    return response.data;
  } catch (error: unknown) {
    const axiosError = error as AxiosError;
    throw axiosError;
  }
};

export const fetchAssociadoPorCpfCnpj = async (cpfCnpj: string): Promise<Associado> => {
  if (!cpfCnpj) {
    throw new Error("O CPF/CNPJ é obrigatório.");
  }

  try {
    const response = await http.get(`/associado/buscar-por-cpf-cnpj/${cpfCnpj}`);

    return response.data;
  } catch (error: unknown) {
    const axiosError = error as AxiosError;
    throw axiosError;
  }
};

export const searchAssociadosByNome = async (
  nome: string,
  page: number = 1,
  limit: number = 10,
): Promise<SearchAssociadosResponse> => {
  const q = (nome ?? '').trim();
  if (!q) throw new Error('Parâmetro "nome" é obrigatório');

  try {
    const res = await http.get('/associado/search', {
      params: { nome: q, page, limit },
    });

    return res.data;
  } catch (error: unknown) {
    const axiosError = error as AxiosError<any>;
    throw new Error(axiosError.response?.data?.message || 'Erro ao buscar associados');
  }
};

/**
 * Tipo do registro no acompanhamento de envios (at-28). Espelha
 * `TipoRegistroEnvio` do backend (`src/enum/registro-envio.enums.ts`).
 */
export type TipoRegistroEnvio =
  | 'ADESAO'
  | 'SUBSTITUICAO_VEICULO'
  | 'TROCA_TITULAR'
  | 'SUBSTITUICAO_E_TROCA_TITULAR';

/**
 * Filtro de situação (at-28). PENDENTE = tem qualquer coisa por enviar ao SGA;
 * ENVIADO = está inteiramente no SGA. Espelha `SituacaoEnvioFiltro` do backend.
 */
export type SituacaoEnvioFiltro = 'TODOS' | 'PENDENTE' | 'ENVIADO';

export interface CarroEnvioSga {
  id: string;
  placa: string;
  modelo: string;
  codigoVeiculo: string | null;
  addedToSga: boolean;
  // at-28 — derivado no backend a partir dos marcadores de substituição do Carro
  tipo: TipoRegistroEnvio;
  placaAntiga: string | null;
  codigoAssociadoNovo: string | null;
  /** at-46: motocicleta elétrica — a "placa" é a identificação gerada pelo SAAC. */
  bikeEletrica?: boolean;
}

export interface AssociadoEnvioSga {
  id: string;
  nomeCompleto: string;
  cpfCnpj: string;
  codigoAssociado: string | null;
  addedToSga: boolean;
  createdAt: string;
  consultor: string;
  carros: CarroEnvioSga[];
}

export interface EnviosSgaResponse {
  totalItems: number;
  page: number;
  limit: number;
  totais: { associadosPendentes: number; veiculosPendentes: number };
  items: AssociadoEnvioSga[];
}

export const fetchEnviosSga = async (
  page: number = 1,
  limit: number = 10,
  busca?: string,
  situacao: SituacaoEnvioFiltro = 'TODOS',
  tipo?: TipoRegistroEnvio,
): Promise<EnviosSgaResponse> => {
  try {
    const termo = busca?.trim();
    const res = await http.get('/associado/envios-sga', {
      params: {
        page,
        limit,
        ...(termo ? { busca: termo } : {}),
        ...(situacao && situacao !== 'TODOS' ? { situacao } : {}),
        ...(tipo ? { tipo } : {}),
      },
    });

    return res.data;
  } catch (error: unknown) {
    const axiosError = error as AxiosError<any>;
    throw new Error(axiosError.response?.data?.message || 'Erro ao buscar acompanhamento de envios ao SGA');
  }
};
